import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useCallback } from 'react';
import { useRecoilValue } from 'recoil';

import PaAdhocEndPage from '../patient/adhoc/PaAdhocEndPage';
import PaAdhocHomePage from '../patient/adhoc/PaAdhocHomePage';
import PaSurveyEndPage from '../patient/survey/PaSurveyEndPage';
import PaSurveyHomePage from '../patient/survey/PaSurveyHomePage';
import PaAthenaInfoCheck from '../patient/survey/PaAthenaInfoCheck';
import PaTreePage from '../patient/tree/PaTreePage';
import { ThemeProvider } from '@emotion/react';
import { patientTheme } from './mui-theme';
import PaSurveyPausePage from '../patient/survey/PaSurveyPausePage';
import PaSurveyTerminatedPage from '../patient/survey/PaSurveyTerminatedPage';
import { AthenaServices } from '../lib/athena/athena-services';
import { useSurveyState, AidState } from '../patient/hooks/use-survey-state';
import { usePaSurveyParams } from '../report/use-pa-survey-params';
import { useReportProviderState } from '../patient/hooks/use-report';
import envs from '../common/envs';

export default function AppRoutesPatient() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, shortId } = usePaSurveyParams();
  const [{ surveyState, reportState }, { setSurveyState, updateVisit }] = useSurveyState();
  const [, { resetAllStates }] = useReportProviderState();
  const encounterCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousAidRef = useRef<string | null>(null);
  const previousShortIdRef = useRef<string | null>(null);
  const searchParams = new URLSearchParams(window.location.search);
  // Recoil AidState에서 aid 가져오기 (query params에 없어도 저장된 값 사용)
  const savedAid = useRecoilValue(AidState);
  
  // 설문이 끝나는 페이지인지 확인하는 함수
  const isSurveyEndPage = useCallback(() => {
    const {pathname} = location;
    return pathname.includes('/finished') || pathname.includes('/pause') || pathname.includes('/terminated');
  }, [location.pathname]);
  
  // 설문 상태 초기화 함수
  const clearSurveyState = useCallback(() => {
    try {
      // 현재 patientDetails와 encounterId 보존 (이미 설정된 환자 정보는 유지)
      const currentPatientDetails = surveyState?.patientDetails;
      const currentEncounterId = surveyState?.encounterId;
      
      // recoil-persist로 저장된 설문 상태들 정리
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('AdminPatientSurveyState') || 
          key.includes('PaReportState') ||
          key.includes('PaSurveyState') ||
          key.includes('PaErrorState')
          // AidState는 제외 - aid 변경 시에도 유지되어야 함
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Recoil 상태들도 초기화
      resetAllStates();
      
      // surveyState 초기화 (patientDetails와 encounterId는 보존)
      setSurveyState({
        adaptedTreeKeys: [],
        curTreeKey: undefined,
        checkFlag: undefined,
        patientDetails: currentPatientDetails, // 환자 정보 유지
        encounterId: currentEncounterId, // encounter ID 유지
        encounterCheckStatus: undefined,
        encounterCheckReason: undefined,
      });
      
      console.log('설문 상태 초기화 완료 - 재작성 가능 (환자 정보 유지)');
    } catch (err) {
      console.warn('설문 상태 초기화 중 오류:', err);
    }
  }, [resetAllStates, setSurveyState, surveyState?.patientDetails, surveyState?.encounterId]);
  
  // aid 및 shortId 변경 감지 및 localStorage 정리 (새 설문 링크로 재작성 가능하도록)
  // 기본 URL(/s/:orgId/:shortId)일 때만 실행
  useEffect(() => {
    // 기본 URL이 아니면 실행하지 않음 (PaSurveyHomePage에서만 작동)
    const isBaseUrl = location.pathname === `/s/${orgId}/${shortId}` || 
                      location.pathname === `/s/${orgId}/${shortId}/`;
    
    if (!isBaseUrl) {
      return;
    }
    
    const currentAid = searchParams.get('aid');
    const currentShortId = shortId || null;
    
    // query params에 aid가 없으면 Recoil AidState에서 가져온 값 사용 (stage-register 이후에도 aid 유지)
    const effectiveAid = currentAid || savedAid || null;
    
    // 이전 effective aid 값 (query params 또는 저장된 값)
    const previousEffectiveAid = previousAidRef.current || savedAid || null;
    
    // aid가 실제로 변경되었는지 확인 (query params에서 사라진 것은 변경으로 간주하지 않음)
    // 단, 새로운 aid 값이 query params로 들어온 경우에만 변경으로 간주
    const aidChanged = currentAid !== null && previousEffectiveAid !== null && currentAid !== previousEffectiveAid;
    const shortIdChanged = previousShortIdRef.current !== null && previousShortIdRef.current !== currentShortId;
    
    // 설문이 끝나는 페이지가 아니고, 실제로 aid가 변경되었거나 shortId가 변경되었으면 상태 초기화
    if (!isSurveyEndPage() && (aidChanged || shortIdChanged)) {
      console.log('새 설문 링크 감지 (기본 URL):', {
        currentAid,
        savedAid,
        effectiveAid,
        previousEffectiveAid,
        aidChanged,
        shortId: { from: previousShortIdRef.current, to: currentShortId, changed: shortIdChanged }
      });
      
      try {
        // 설문 상태 확인 (aid가 있는 경우)
        if (effectiveAid && aidChanged) {
          console.log('aid 변경 감지 - 상태 초기화');
          clearSurveyState();
        } else if (shortIdChanged) {
          // shortId만 변경된 경우 상태 초기화
          console.log('shortId 변경 - 상태 초기화');
          clearSurveyState();
        }
        // aid가 query params에서 사라진 경우는 상태 초기화하지 않음 (stage-register 등에서 aid가 URL에서 제거되더라도 유지)
      } catch (err) {
        console.warn('설문 링크 변경 감지 중 오류:', err);
      }
    }
    
    // 이전 값 업데이트 (query params에 aid가 있으면 그것을, 없으면 저장된 값 유지)
    previousAidRef.current = currentAid || savedAid || null;
    previousShortIdRef.current = currentShortId;
  }, [searchParams, shortId, location.pathname, orgId, isSurveyEndPage, clearSurveyState, savedAid]);

  // SMS 발송 함수
  const sendSms = async (phoneNumber: string, message: string) => {
    try {
      const response = await fetch(`${envs.v2BackendUrl}/v2/sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 필요한 경우 인증 헤더 추가
        },
        body: JSON.stringify({
          to: phoneNumber,
          body: message,
        }),
      });

      if (!response.ok) {
        console.error('Failed to send SMS:', response.statusText);
      } else {
        console.log('SMS sent successfully');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
    }
  };

  // encounter 상태 체크 함수
  const checkEncounterStatus = async (encounterId: string): Promise<boolean> => {
    try {
      // 설문이 끝나는 페이지에 있으면 체크 중단
      if (isSurveyEndPage()) {
        console.log('설문이 끝나는 페이지에 있어서 encounter 체크를 중단합니다.');
        if (encounterCheckIntervalRef.current) {
          clearInterval(encounterCheckIntervalRef.current);
          encounterCheckIntervalRef.current = null;
        }
        // Safe localStorage removal with error handling
        try {
          localStorage.removeItem('encounterCheck');
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }
        return false;
      }
      
      // 중복 실행 방지: 이미 완료된 상태(pause/terminated)로 넘어간 경우 더 이상 체크하지 않음
      // localStorage를 통해 상태 확인
      try {
        const savedCheck = localStorage.getItem('encounterCheck');
        if (savedCheck) {
            const parsedData = JSON.parse(savedCheck);
            if (parsedData.status === 'completed' || parsedData.status === 'timeout' || parsedData.status === 'with_staff') {
                console.log('Encounter check already completed/terminated. Stopping check.');
                if (encounterCheckIntervalRef.current) {
                    clearInterval(encounterCheckIntervalRef.current);
                    encounterCheckIntervalRef.current = null;
                }
                return false;
            }
        }
      } catch (e) {
          // ignore error
      }

      const result = await AthenaServices.checkEncounterStatus(encounterId);
      
      if (result.success && result.data) {
        const patientStatus = result.data[0].patientstatus;
        console.log(`Encounter ${encounterId} 상태:`, patientStatus);
        
        // "With staff" 혹은 "Checked Out" 상태로 변경되면 즉시 PaSurveyPausePage로 이동
        if (patientStatus === 'With Staff' || patientStatus === 'Checked Out') {
          // 서버의 patient-visit status를 'with_staff'로 업데이트
          updateVisit(reportState, { 
            data: { 
              status: 'with_staff' as any 
            } 
          });

          setSurveyState((st) => ({
            ...st,
            encounterCheckReason: 'with_staff'
          }));
          
          if (encounterCheckIntervalRef.current) {
            clearInterval(encounterCheckIntervalRef.current);
            encounterCheckIntervalRef.current = null;
          }
          
          // localStorage 상태 업데이트 (SMS 중복 발송 방지)
          try {
            const savedCheck = localStorage.getItem('encounterCheck');
            if (savedCheck) {
                const parsedData = JSON.parse(savedCheck);
                parsedData.status = 'with_staff'; // 상태 변경
                localStorage.setItem('encounterCheck', JSON.stringify(parsedData));
            }
          } catch (e) {
            console.warn('Failed to update localStorage:', e);
          }

          // SMS 발송
          const mobilePhone = surveyState?.patientDetails?.mobilephone;
          if (mobilePhone) {
             // E.164 포맷 확인 및 변환 (간단한 예시, 필요 시 더 정교한 로직 추가)
             let formattedPhone = mobilePhone.replace(/\D/g, ''); // 숫자만 추출
             if (formattedPhone.length === 10) {
                formattedPhone = `+1${formattedPhone}`;
             } else if (formattedPhone.length > 10 && !mobilePhone.startsWith('+')) {
                 formattedPhone = `+${formattedPhone}`;
             } else if (mobilePhone.startsWith('+')) {
                 formattedPhone = mobilePhone;
             } else {
                 // 유효하지 않은 번호일 경우 발송 안 함 (또는 로그)
                 console.warn('Invalid phone number format for SMS:', mobilePhone);
                 formattedPhone = null;
             }

             if (formattedPhone) {
                 const message = "Your information has been sent to your documents because your care team is ready to see you and your link above has expired. If you weren’t able to enter details of your symptoms, no worries. Your care team will guide you from here. Do NOT reply to this message.";
                 // 중복 발송 방지를 위해 localStorage 확인은 위에서 처리했으므로 바로 발송
                 // 하지만 안전을 위해 한 번 더 체크하거나 별도 플래그를 둘 수 있음
                 // 여기서는 상태 변경 시점에 한 번만 호출되므로 괜찮음.
                 sendSms(formattedPhone, message);
             }
          }
          
          // localStorage에서 체크 상태 제거
          try {
            localStorage.removeItem('encounterCheck');
          } catch (e) {
            console.warn('Failed to clear localStorage:', e);
          }
          
          // PaSurveyPausePage로 이동
          navigate(`/s/${orgId}/${shortId}/pause`);
          return false; // 체크 중단
        }
      }
      
      return true; // 체크 계속
    } catch (error) {
      console.error('Encounter 상태 체크 실패:', error);
      return true; // 오류가 있어도 체크 계속
    }
  };

  // encounter 상태 주기적 체크 시작
  const startEncounterStatusCheck = (encounterId: string) => {
    // 설문이 끝나는 페이지에 있으면 체크 시작하지 않음
    if (isSurveyEndPage()) {
      console.log('설문이 끝나는 페이지에 있어서 encounter 체크를 시작하지 않습니다.');
      return;
    }

    // 이미 체크 중이면 중복 실행 방지
    if (encounterCheckIntervalRef.current) {
      return;
    }

    console.log(`Encounter ${encounterId} 상태 체크 시작`);
    
    // localStorage에 체크 상태 저장 (안전한 저장)
    const checkData = {
      encounterId,
      startTime: Date.now(),
      status: 'checking',
      count: 0
    };
    
    try {
      localStorage.setItem('encounterCheck', JSON.stringify(checkData));
    } catch (e) {
      console.warn('Failed to save to localStorage:', e);
      // localStorage 실패 시 체크를 시작하지 않음
      return;
    }
    
    // 즉시 한 번 체크
    checkEncounterStatus(encounterId);

    // 1분마다 체크 (30분간, 총 30회)
    const intervalMs = 60 * 1000; // 1분
    const maxCount = 30;
    let count = 0;

    encounterCheckIntervalRef.current = setInterval(async () => {
      try {
        count++;
        
        // 이미 종료된 상태인지 확인 (다른 탭 등에서 종료되었을 경우)
        try {
            const savedCheck = localStorage.getItem('encounterCheck');
            if (!savedCheck) {
                 // localStorage가 지워졌다면 종료된 것으로 간주
                 if (encounterCheckIntervalRef.current) {
                    clearInterval(encounterCheckIntervalRef.current);
                    encounterCheckIntervalRef.current = null;
                 }
                 return;
            }
            const parsed = JSON.parse(savedCheck);
            if (parsed.status !== 'checking') {
                 if (encounterCheckIntervalRef.current) {
                    clearInterval(encounterCheckIntervalRef.current);
                    encounterCheckIntervalRef.current = null;
                 }
                 return;
            }
        } catch (e) {
            // ignore
        }

        // localStorage 업데이트 (안전한 업데이트)
        const updatedCheckData = {
          encounterId,
          startTime: checkData.startTime,
          status: 'checking',
          count
        };
        
        try {
          localStorage.setItem('encounterCheck', JSON.stringify(updatedCheckData));
        } catch (e) {
          console.warn('Failed to update localStorage:', e);
        }
        
        const shouldContinue = await checkEncounterStatus(encounterId);
        
        if (!shouldContinue || count >= maxCount) {
          // 체크 종료
          if (encounterCheckIntervalRef.current) {
            clearInterval(encounterCheckIntervalRef.current);
            encounterCheckIntervalRef.current = null;
          }
          
          if (count >= maxCount) {
            // 30분 타임아웃
            // 서버의 patient-visit status를 'timeout'으로 업데이트
            updateVisit(reportState, { 
              data: { 
                status: 'timeout' as any 
              } 
            });

            setSurveyState((st) => ({
              ...st,
              encounterCheckReason: 'timeout'
            }));
            
            // 타임아웃 상태로 localStorage 업데이트 (중복 처리 방지)
            try {
                const savedCheck = localStorage.getItem('encounterCheck');
                if (savedCheck) {
                    const parsedData = JSON.parse(savedCheck);
                    parsedData.status = 'timeout';
                    localStorage.setItem('encounterCheck', JSON.stringify(parsedData));
                }
            } catch (e) {
                console.warn('Failed to update localStorage:', e);
            }

            console.log(`Encounter ${encounterId} 체크 완료 (30분 경과)`);

            // SMS 발송 (타임아웃)
            const mobilePhone = surveyState?.patientDetails?.mobilephone;
            if (mobilePhone) {
                // E.164 포맷 확인 및 변환
                let formattedPhone = mobilePhone.replace(/\D/g, '');
                if (formattedPhone.length === 10) {
                    formattedPhone = `+1${formattedPhone}`;
                } else if (formattedPhone.length > 10 && !mobilePhone.startsWith('+')) {
                    formattedPhone = `+${formattedPhone}`;
                } else if (mobilePhone.startsWith('+')) {
                    formattedPhone = mobilePhone;
                } else {
                     console.warn('Invalid phone number format for SMS:', mobilePhone);
                     formattedPhone = null;
                }

                if (formattedPhone) {
                    const message = "You’ve reached the 30-minute limit and your link above has expired. The information you have submitted so far will be sent to your documents. If you weren’t able to enter details of your symptoms, no worries. Your care team will guide you from here. Do NOT reply to this message.";
                    sendSms(formattedPhone, message);
                }
            }

            // localStorage에서 체크 상태 제거
            try {
              localStorage.removeItem('encounterCheck');
            } catch (e) {
              console.warn('Failed to clear localStorage:', e);
            }

            // 30분 타임아웃 시 pause 페이지로 이동
            navigate(`/s/${orgId}/${shortId}/pause`);
          }
        }
      } catch (err) {
        console.error('Encounter 상태 체크 중 오류:', err);
      }
    }, intervalMs);
  };

  // localStorage에서 encounter 체크 상태 복원
  useEffect(() => {
    // 설문이 끝나는 페이지에 있으면 체크 복원하지 않음
    if (isSurveyEndPage()) {
      console.log('설문이 끝나는 페이지에 있어서 encounter 체크를 복원하지 않습니다.');
      return;
    }

    // 안전한 localStorage 데이터 읽기 및 검증
    let savedCheck: string | null = null;
    try {
      savedCheck = localStorage.getItem('encounterCheck');
    } catch (e) {
      console.warn('Failed to read from localStorage:', e);
      return;
    }
    
    if (savedCheck && !encounterCheckIntervalRef.current) {
      try {
        if (isSurveyEndPage()) {
          console.log('설문이 끝나는 페이지에 있어서 encounter 체크를 복원하지 않습니다.');
          return;
        }

        // 안전한 JSON 파싱 및 데이터 검증
        const parsedData = JSON.parse(savedCheck);
        
        // 필수 필드 검증
        if (!parsedData || typeof parsedData !== 'object' || 
            !parsedData.encounterId || typeof parsedData.encounterId !== 'string' ||
            !parsedData.startTime || typeof parsedData.startTime !== 'number' ||
            typeof parsedData.count !== 'number') {
          throw new Error('Invalid data structure in localStorage');
        }
        
        const { encounterId, startTime, count } = parsedData;
        const elapsed = Date.now() - startTime;
        const maxDuration = 30 * 60 * 1000; // 30분
        
        if (elapsed < maxDuration) {
          console.log(`Encounter 체크 상태 복원: ${encounterId}, 경과시간: ${Math.round(elapsed / 1000)}초`);
          startEncounterStatusCheck(encounterId);
        } else {
          // 30분 초과 시 localStorage 정리
          try {
            localStorage.removeItem('encounterCheck');
          } catch (e) {
            console.warn('Failed to clear localStorage:', e);
          }
          console.log('Encounter 체크 만료로 인한 정리');
        }
      } catch (error) {
        console.error('localStorage에서 encounter 체크 상태 복원 실패:', error);
        try {
          localStorage.removeItem('encounterCheck');
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }
      }
    }
  }, [location.pathname]);

  // surveyState에 encounterId가 있고 체크가 시작되지 않았으면 체크 시작
  useEffect(() => {
    // 설문이 끝나는 페이지에 있으면 체크 시작하지 않음
    if (isSurveyEndPage()) {
      return;
    }

    const encounterId = surveyState?.encounterId;
    const encounterCheckStatus = surveyState?.encounterCheckStatus;
    
    if (encounterId && encounterCheckStatus === 'checking' && !encounterCheckIntervalRef.current) {
      startEncounterStatusCheck(encounterId);
    }
  }, [surveyState?.encounterId, surveyState?.encounterCheckStatus, location.pathname]);

  // 컴포넌트 언마운트 시 interval 정리
  useEffect(() => {
    return () => {
      if (encounterCheckIntervalRef.current) {
        clearInterval(encounterCheckIntervalRef.current);
      }
    };
  }, []);

  // NOTE update use-pa-survey-params when route changes
  return (
    <ThemeProvider theme={patientTheme}>
      <Routes>
        <Route path=':orgId/:shortId/athena-check' element={<PaAthenaInfoCheck />} />
        <Route path=':orgId/:shortId/adhoc/:adhocId/finished' element={<PaAdhocEndPage />} />
        <Route path=':orgId/:shortId/adhoc/:adhocId' element={<PaAdhocHomePage />} />
        <Route path=':orgId/:shortId/finished' element={<PaSurveyEndPage />} />
        <Route path=':orgId/:shortId/terminated' element={<PaSurveyTerminatedPage />} />
        <Route path=':orgId/:shortId/pause' element={<PaSurveyPausePage />} />
        <Route path=':orgId/:shortId/addon/:referralMessageId' element={<PaSurveyHomePage />} />
        <Route path=':orgId/:shortId/:treeKey' element={<PaTreePage />} />
        <Route path=':orgId/:shortId' element={<PaSurveyHomePage />} />
      </Routes>
    </ThemeProvider>
  );
}
